"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, ApiError } from "@/lib/api";
import { t } from "@/lib/i18n";

// Mirrors the password floor the backend enforces (routes.py::register).
// Checking it here too turns a round-trip and an error toast into inline
// feedback while the field is still focused.
const MIN_PASSWORD = 8;

export default function RegisterPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit =
    email.trim() !== "" &&
    password.length >= MIN_PASSWORD &&
    confirm === password &&
    !loading;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      const user = await api.register(email, password, name.trim() || undefined);
      // Same reason as the login page: seed ["me"] so useRequireAuth on
      // /projects doesn't read SiteNav's cached logged-out error and bounce
      // straight back before its own refetch resolves.
      queryClient.setQueryData(["me"], user);
      router.push("/projects");
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Could not reach the server",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t("Create your account")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name">{t("Name (optional)")}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">{t("Email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="password">{t("Password")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <p
                className={
                  tooShort ? "text-xs text-destructive" : "text-xs text-muted-foreground"
                }
              >
                At least {MIN_PASSWORD} characters.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="confirm">{t("Confirm password")}</Label>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
              {mismatch && (
                <p className="text-xs text-destructive">{t("Passwords do not match.")}</p>
              )}
            </div>

            <Button type="submit" disabled={!canSubmit} className="mt-2">
              {loading ? t("Creating account...") : t("Create account")}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link href="/login" className="text-primary hover:underline">
                {t("Sign in")}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
